import React, { useContext, useEffect, useState } from 'react';
import { useFlag, useUnleashContext } from '@unleash/proxy-client-react';
import { UtilisateurCourantContext } from 'context/UtilisateurCourantContext';

import { 
  Paper, 
  Typography, 
  Box, 
  Grid, 
  Tooltip
} from '@mui/material';

import UtilisateurSearch from 'components/Shared/UtilisateurSearch';

import { Link as RouterLink } from 'react-router-dom';
import Link from '@mui/material/Link';

// Type pour les feature flags
type FeatureFlag = {
  name: string;
  description: string;
  flagKey: string;
};

// Composant pour afficher un voyant rouge/vert
const StatusIndicator: React.FC<{ isEnabled: boolean }> = ({ isEnabled }) => {
  return (
    <Box
      sx={{
        width: 20,
        height: 20,
        borderRadius: '50%',
        backgroundColor: isEnabled ? '#4caf50' : '#f44336',
        display: 'inline-block',
        marginRight: 1
      }}
    />
  );
};

// Composant pour un feature flag individuel
const FeatureFlagItem: React.FC<FeatureFlag> = ({ name, description, flagKey }) => {
  // Utilisation du hook useFlag d'Unleash pour vérifier si le flag est activé
  const isEnabled = useFlag(flagKey);
  
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', p: 1, borderBottom: '1px solid #eee' }}>
      <Tooltip title={isEnabled ? 'Activé' : 'Désactivé'}>
        <Box>
          <StatusIndicator isEnabled={isEnabled} />
        </Box>
      </Tooltip>
      <Box>
        <Typography variant="subtitle1">{name}</Typography>
        <Typography variant="body2" color="textSecondary">
          {description}
        </Typography>
      </Box>
    </Box>
  );
};

// Composant principal
const AdministrationFeatureFlags: React.FC = () => {
  // Récupération de l'utilisateur courant depuis le contexte
  const { utilisateur } = useContext(UtilisateurCourantContext);
  
  // État pour stocker l'ID de l'utilisateur sélectionné
  const [selectedUserId, setSelectedUserId] = useState<number>(0);
  
  // Namespace pour le composant UtilisateurSearch
  const namespace = 'administrationFeatureFlags';
  
  // Récupération de la fonction de mise à jour du contexte Unleash
  const updateContext = useUnleashContext();
  
  // Initialisation de l'utilisateur sélectionné avec l'utilisateur courant
  useEffect(() => {
    if (utilisateur && utilisateur.id && selectedUserId === 0) {
      setSelectedUserId(utilisateur.id);
    }
  }, [utilisateur, selectedUserId]);
  
  // Mise à jour du contexte Unleash avec l'ID de l'utilisateur
  useEffect(() => {
    if (selectedUserId !== 0) {
      updateContext({ userId: `${selectedUserId}` });
    }
  }, [selectedUserId, updateContext]);

  // Gestion du changement d'utilisateur
  const handleUserChange = (id: number, _codePO: string, _nom: string, _prenom: string) => {
    setSelectedUserId(id);
  };

  // Liste des feature flags à vérifier
    const featureFlags: FeatureFlag[] = [
        {
            name: 'Flag de test',
            description: 'Flag pour vérifier, y compris en production, que les flags unleash fonctionnent.',
            flagKey: 'dummy_flag'
        }
  ];

  return (
    <Paper elevation={3} sx={{ p: 3, mt: 2 }}>
      <Typography variant="h5" gutterBottom>
        Feature Flags
      </Typography>
      <Typography variant="body2" color="textSecondary" paragraph>
        Cette page affiche l'état des feature flags du système. Un voyant vert indique que la fonctionnalité est activée, un voyant rouge indique qu'elle est désactivée.
      </Typography>


      <Link
          to={`https://eu.app.unleash-hosted.com/eubb1046/projects/portail-apres-vente`}
          underline="hover"
          component={RouterLink}
          sx={{ fontWeight: 'bold' }}
      >
          {"unleash"}
      </Link>

      <Link></Link>

      {/* Sélecteur d'utilisateur pour le contexte Unleash */}
      <Box sx={{ m: 2 }}>
        <UtilisateurSearch
          namespace={namespace}
          onChange={handleUserChange}
          isRequired={false}
          autoFocus={false}
          label="Utilisateur pour le contexte Unleash"
        />
      </Box>

      <Grid container spacing={2} sx={{ mt: 2 }}>
        {featureFlags.map((flag, index) => (
          <Grid item xs={12} key={index}>
            <FeatureFlagItem 
              name={flag.name} 
              description={flag.description} 
              flagKey={flag.flagKey} 
            />
          </Grid>
        ))}
      </Grid>
    </Paper>
  );
};

export default AdministrationFeatureFlags;
